import { z } from "zod";

// Custom ObjectId validation function
const isValidObjectId = (value) => /^[0-9a-fA-F]{24}$/.test(value);

// Regex patterns
const pincodeRegex = /^\d{6}$/;
const phoneRegex = /^\d{10}$/;
const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const shipmentRegex = /^U\d{8}-P\d{4}-\d{6}$/;

// Helper function to parse date strings safely
const parseDate = (value) => {
  if (typeof value === "string") {
    const date = new Date(value);
    if (!isNaN(date.getTime())) return date;
  }
  return value; // Allow native Date objects to pass through
};

// Helper function to calculate total order value
const calculateTotalOrderValue = (products) => {
  if (!Array.isArray(products) || products.length === 0) return 0;
  return products.reduce((sum, product) => {
    const orderValue = Number(product?.orderValue) || 0;
    return sum + orderValue;
  }, 0);
};

const dateSchema = z
  .union([
    z.string().min(1, { message: "Date is required" }).transform(parseDate),
    z.date(),
  ])
  .refine((date) => date instanceof Date && !isNaN(date), {
    message: "Invalid date format",
  });

const optionalDateSchema = z
  .union([
    z.string().transform((val) => (val?.trim() === "" ? "" : parseDate(val))),
    z.date(),
    z.null(),
  ])
  .optional()
  .transform((val) => (val === undefined || val === null ? "" : val))
  .refine((date) => date === "" || (date instanceof Date && !isNaN(date)), {
    message: "Invalid date format",
  });

// Phone schema
const PhoneSchema = z
  .object({
    phoneNumber: z.string().optional(), // Start as optional
    phoneType: z.enum(["Primary", "Secondary"], {
      invalid_type_error: "Phone type must be 'Primary' or 'Secondary'",
      required_error: "Phone type is required",
    }),
  })
  .superRefine((data, ctx) => {
    const { phoneNumber, phoneType } = data;

    // If phoneType is "Primary", phoneNumber is required
    if (phoneType === "Primary") {
      if (!phoneNumber || phoneNumber.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Primary phone number is required",
          path: ["phoneNumber"], // Specify the field causing the issue
        });
      }
    }

    // If phoneNumber is provided, validate its format
    if (phoneNumber && phoneNumber.trim() !== "") {
      if (!phoneRegex.test(phoneNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid phone number format",
          path: ["phoneNumber"], // Specify the field causing the issue
        });
      }
    }
  });

// Address line schema
const AddressLineSchema = z.object({
  line1: z.string().min(1, "Address is required"),
  line2: z.string().optional(),
});

// Delivery address schema
const DeliveryAddressSchema = z.object({
  lines: z
    .array(AddressLineSchema)
    .min(1, "At least one address line is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  pincode: z.string().regex(pincodeRegex, "Invalid pincode format"),
  landmark: z.string().optional(),
  customerName: z.string().min(1, "Customer name is required"),
  gstNumber: z
    .string()
    .optional()
    .nullable()
    .superRefine((gst, ctx) => {
      if (!gst) return;
      if (!gstRegex.test(gst)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid GST number format",
        });
      }
    }),
});

// Product schema
const ProductSchema = z.object({
  productName: z.string().min(1, "Product name is required"),
  quantity: z.coerce.number().positive("Quantity must be positive"),
  hsn: z.string().optional(),
  sku: z.string().min(1, "SKU is required"),
  orderValue: z.coerce.number().positive("Order value must be positive"),
});

// Dimensions schema
const positiveNumberField = (fieldLabel) =>
  z.coerce
    .number({
      required_error: `${fieldLabel} is required`,
      invalid_type_error: `${fieldLabel} must be a number`,
    })
    .positive(`${fieldLabel} must be positive`);

const DimensionsSchema = z.object({
  length: positiveNumberField("Length"),
  breadth: positiveNumberField("Breadth"),
  height: positiveNumberField("Height"),
});

// Invoice schema with strict date parsing
const InvoiceSchema = z.object({
  invoiceDate: dateSchema,
  invoiceNumber: z.string().min(1, "Invoice number is required"),
});

const EbnSchema = z
  .object({
    hasEBN: z.boolean().default(false),
    ebnNumber: z.string().optional(),
    ebnExpiryDate: optionalDateSchema,
  })
  .transform((data) => {
    // Explicitly check for non-empty string and valid date
    const isValidEbnNumber = Boolean(
      data.ebnNumber && data.ebnNumber.trim().length > 0
    );
    const isValidEbnExpiryDate = Boolean(
      data.ebnExpiryDate instanceof Date && !isNaN(data.ebnExpiryDate.getTime())
    );

    return {
      ...data,
      hasEBN: Boolean(isValidEbnNumber && isValidEbnExpiryDate), // Ensure boolean conversion
    };
  });

const paymentModeSchema = z
  .string()
  .min(1, { message: "Payment mode is required" }) // Ensures value is present
  .refine((val) => ["COD", "Prepaid"].includes(val), {
    message: "Invalid payment mode. Must be 'cod' or 'prepaid'",
  });

// Main order schema with strict date parsing
const CreateOrderSchema = z
  .object({
    orderId: z.string().min(1, "Order ID is required"),
    orderDate: dateSchema,
    paymentMode: paymentModeSchema,
    hasShipmentId: z.boolean().default(false).optional(),
    shipmentId: z
      .string()
      .optional()
      .transform((data) => {
        if (!data || data.trim() === "") {
          return { value: "", hasShipmentId: false };
        }
        const isValidFormat = shipmentRegex.test(data);
        return {
          value: data,
          hasShipmentId: isValidFormat,
          isValidFormat,
        };
      })
      .superRefine((data, ctx) => {
        if (data.value && !data.isValidFormat) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid ShippingId Format",
          });
          data = data.value;
        }
      })
      .transform(data => data.value),
    deliveryAddress: DeliveryAddressSchema,
    deliveryPhones: z
      .array(PhoneSchema)
      .min(1, "At least one phone number is required")
      .refine(
        (phones) => phones.some((phone) => phone.phoneType === "Primary"),
        "Primary phone number is required"
      )
      .transform((phones) => {
        // Filter out Secondary phones with empty phoneNumber
        return phones.filter((phone) => {
          if (
            phone.phoneType === "Secondary" &&
            (!phone.phoneNumber || phone.phoneNumber.trim() === "")
          ) {
            return false; // Remove this object
          }
          return true; // Keep this object
        });
      }),
    invoice: InvoiceSchema,
    ebn: EbnSchema,
    physicalWeight: positiveNumberField("PhysicalWeight"),
    dimensions: DimensionsSchema,
    products: z.array(ProductSchema).min(1, "You must add at least one product. Click the Add button to continue."),
  })
  .superRefine((data, ctx) => {
    const totalValue = calculateTotalOrderValue(data.products);

    // GST validation when order value is above ₹50,000
    if (
      totalValue >= 50000 &&
      (!data.deliveryAddress.gstNumber ||
        data.deliveryAddress.gstNumber.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "GST number is required for orders above ₹50,000",
        path: ["deliveryAddress", "gstNumber"],
      });
    }

    if (
      (data.shipmentId !== undefined && data.shipmentId === "") ||
      data.shipmentId.length > 0
    ) {
      if (data.shipmentId.length > 0) data.hasShipmentId = true;
      data.hasException = false;
      data.exceptionMessage = []
    }

    // EBN Validation when order value exceeds ₹50,000
    if (totalValue >= 50000) {
      if (!data.ebn?.ebnNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "EBN number is required for orders above ₹50,000",
          path: ["ebn", "ebnNumber"],
        });
      }
      if (!data.ebn?.ebnExpiryDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "EBN expiry date is required for orders above ₹50,000",
          path: ["ebn", "ebnExpiryDate"],
        });
      }
    }
  });

export default CreateOrderSchema;
